import { useGitpodAPI } from "@/hooks/use-gitpod-api";
import { keyWithPrincipal } from "@/queries/principal-key";
import { Timestamp, toPlainMessage, type PlainMessage } from "@bufbuild/protobuf";
import type {
    GetCreditUsageReportResponse,
    DailyCreditUsage,
    UserCreditUsage,
    CreditsByType,
} from "gitpod-next-api/gitpod/v1/billing_pb";
import { DateRange } from "gitpod-next-api/gitpod/v1/usage_pb";
import { queryOptions, useQuery, useMutation } from "@tanstack/react-query";

export type PlainGetCreditUsageReportResponse = PlainMessage<GetCreditUsageReportResponse>;
export type PlainDailyCreditUsage = PlainMessage<DailyCreditUsage>;
export type PlainUserCreditUsage = PlainMessage<UserCreditUsage>;
export type PlainCreditsByType = PlainMessage<CreditsByType>;

const enterpriseUsageKeys = {
    report: (organizationId: string, startISO: string, endISO: string) =>
        keyWithPrincipal(["enterprise-usage", "report", organizationId, startISO, endISO]),
};

export function useEnterpriseCreditUsageReportOptions(organizationId: string, startDate: Date, endDate: Date) {
    const api = useGitpodAPI();
    const startISO = startDate.toISOString();
    const endISO = endDate.toISOString();

    return queryOptions({
        queryKey: enterpriseUsageKeys.report(organizationId, startISO, endISO),
        queryFn: async () => {
            const response = await api.billingService.getCreditUsageReport({
                organizationId,
                dateRange: new DateRange({
                    startTime: Timestamp.fromDate(startDate),
                    endTime: Timestamp.fromDate(endDate),
                }),
            });
            return toPlainMessage(response);
        },
        throwOnError: false,
        enabled: !!organizationId,
    });
}

export function useEnterpriseCreditUsageReport(organizationId: string, startDate: Date, endDate: Date) {
    return useQuery(useEnterpriseCreditUsageReportOptions(organizationId, startDate, endDate));
}

export function useExportCreditUsage() {
    const api = useGitpodAPI();

    return useMutation({
        mutationFn: async ({
            organizationId,
            startDate,
            endDate,
        }: {
            organizationId: string;
            startDate: Date;
            endDate: Date;
        }) => {
            const response = await api.billingService.getCreditUsageExport({
                organizationId,
                dateRange: new DateRange({
                    startTime: Timestamp.fromDate(startDate),
                    endTime: Timestamp.fromDate(endDate),
                }),
            });
            return response.downloadUrl;
        },
    });
}
